import React, { useEffect, useMemo, useState } from 'react';
import {
  Container, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Switch, Chip, Typography, Box, TextField, Tooltip, CircularProgress, Button,
  Alert, Snackbar, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import Home from './Home';
import NormePjDialog from './NormePjDialog';
import {
  NormePj, EtatNormesPj, LIBELLES_CLES_FIXES,
  estCleFixe, listerNormesPj, etatNormesPj, majNormePj, supprimerNormePj, resumerRegle, messageErreur, outilsManquants
} from '../services/normesPjServices';

// Menu « Pièces jointes » : normes de pièces jointes par visa (formats, poids, dimensions attendus par les portails).
// Dans Demandes, le bouton « Normes PJ » d'un dossier applique la norme de son visa (fenêtre de conformité).

const libelleCle = (cle: string): string => (estCleFixe(cle) ? LIBELLES_CLES_FIXES[cle] : `« ${cle} »`);

const PiecesJointesScreen: React.FC = () => {
  const [normes, setNormes] = useState<NormePj[]>([]);
  const [etat, setEtat] = useState<EtatNormesPj | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<NormePj | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<NormePj | null>(null);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' | 'warning' | 'info' } | null>(null);

  // Appels indépendants : un 503 « tables absentes » sur la liste ne doit pas priver de /etat (bannière rouge)
  const fetchAll = async () => {
    setLoading(true);
    const [n, e] = await Promise.allSettled([listerNormesPj(), etatNormesPj()]);
    if (e.status === 'fulfilled') setEtat(e.value);
    else console.error('Erreur chargement état des normes PJ', e.reason);
    if (n.status === 'fulfilled') setNormes(n.value);
    else {
      console.error('Erreur chargement normes PJ', n.reason);
      setNormes([]);
      if (e.status !== 'fulfilled' || e.value.tables_ok) setToast({ msg: messageErreur(n.reason, 'Chargement des normes impossible'), sev: 'error' });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return normes.filter(
      (n) => n.libelle.toLowerCase().includes(q)
        || n.visas_titres.some((t) => t.toLowerCase().includes(q))
        || `${n.motif_tous} ${n.motif_un_parmi}`.toLowerCase().includes(q)
    );
  }, [normes, search]);

  const toggleActif = async (n: NormePj, actif: boolean) => {
    setNormes((prev) => prev.map((x) => (x.id === n.id ? { ...x, actif: actif ? 1 : 0 } : x))); // maj optimiste
    try {
      await majNormePj(n.id, { actif });
    } catch (e) {
      console.error('Erreur activation norme', e);
      setNormes((prev) => prev.map((x) => (x.id === n.id ? { ...x, actif: actif ? 0 : 1 } : x)));
      setToast({ msg: messageErreur(e, 'Modification impossible'), sev: 'error' });
    }
  };

  const openEdit = (n: NormePj | null) => {
    setSelected(n);
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    const n = confirmDelete;
    setConfirmDelete(null);
    try {
      await supprimerNormePj(n.id);
      setToast({ msg: `Norme « ${n.libelle} » supprimée`, sev: 'success' });
      fetchAll();
    } catch (e) {
      console.error('Erreur suppression norme', e);
      setToast({ msg: messageErreur(e, 'Suppression impossible'), sev: 'error' });
    }
  };

  const manques = etat && etat.tables_ok ? outilsManquants(etat.outils) : [];

  return (
    <>
      <Home />
      <Container maxWidth={false} sx={{ mt: 2, px: { xs: 1, sm: 3 } }}>
        {!loading && !etat && (
          <Alert severity="error" sx={{ mb: 2 }}>
            État du module indisponible (backend injoignable, ou pas encore redémarré depuis l'ajout du module ?).
          </Alert>
        )}
        {etat && !etat.tables_ok && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Normes de pièces jointes désactivées : {etat.raison || 'tables absentes'}. Jouer la migration
            migrations/create_pj_normes.sql ; le module se réactive seul dès que les tables existent.
          </Alert>
        )}
        {manques.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Conversions partiellement indisponibles, outils manquants :
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              {manques.map((m) => <li key={m}>{m}</li>)}
            </Box>
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Pièces jointes</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
            Normes de pièces jointes par visa : formats, poids et dimensions attendus par les portails. Dans Demandes, le
            bouton « Normes PJ » d'un dossier contrôle ses pièces et produit des versions conformes, sans toucher aux originaux.
          </Typography>
          <TextField label="Rechercher" size="small" value={search} onChange={(e) => setSearch(e.target.value)} />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openEdit(null)}
            disabled={!!etat && !etat.tables_ok}
          >
            Nouvelle norme
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Norme</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Visas et mots-clés</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Pièces</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Active</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((n) => {
                  const motif = [n.motif_tous, n.motif_un_parmi ? `(${n.motif_un_parmi.split(',').join(' | ')})` : '']
                    .filter(Boolean).join(' + ');
                  return (
                    <TableRow key={n.id} hover sx={{ opacity: Number(n.actif) === 1 ? 1 : 0.6, verticalAlign: 'top' }}>
                      <TableCell sx={{ fontWeight: 500, minWidth: 180 }}>
                        {n.libelle}
                        {n.note && (
                          <Typography variant="caption" display="block" color="text.secondary">{n.note}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {n.visas_titres.map((t) => (
                            n.titres_absents.includes(t) ? (
                              <Tooltip key={t} title="Titre introuvable dans le catalogue (visa renommé ?) : plus aucun dossier ne sera reconnu par ce titre">
                                <Chip label={t} size="small" color="warning" variant="outlined" />
                              </Tooltip>
                            ) : (
                              <Chip key={t} label={t} size="small" variant="outlined" />
                            )
                          ))}
                          {motif && <Chip label={`mots-clés : ${motif}`} size="small" sx={{ bgcolor: '#e0e0e0' }} />}
                          {n.visas_titres.length === 0 && !motif && (
                            <Typography variant="caption" color="text.secondary">choix manuel uniquement</Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ minWidth: 320 }}>
                        {n.pieces.map((r) => (
                          <Typography key={r.cle} variant="body2" sx={{ mb: 0.25 }}>
                            <strong>{libelleCle(r.cle)}</strong> : {resumerRegle(r) || 'aucune contrainte'}
                          </Typography>
                        ))}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={Number(n.actif) === 1 ? 'Norme active' : 'Norme désactivée : jamais appliquée'}>
                          <Switch size="small" checked={Number(n.actif) === 1} onChange={(e) => toggleActif(n, e.target.checked)} />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Modifier">
                          <IconButton size="small" color="primary" onClick={() => openEdit(n)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton size="small" color="error" onClick={() => setConfirmDelete(n)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {normes.length ? 'Aucune norme ne correspond à la recherche' : 'Aucune norme : créez la première avec « Nouvelle norme »'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>

      <NormePjDialog
        open={dialogOpen}
        norme={selected}
        normes={normes}
        onClose={() => setDialogOpen(false)}
        onSaved={fetchAll}
      />

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Confirmez la suppression</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Supprimer la norme « {confirmDelete ? confirmDelete.libelle : ''} » ? Ses visas redeviennent libres pour une
            autre norme. Les versions conformes déjà produites restent dans les dossiers.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} color="primary">Annuler</Button>
          <Button onClick={handleDelete} color="error" autoFocus>Confirmer</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </>
  );
};

export default PiecesJointesScreen;
